import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Paper, Typography } from '@material-ui/core';
import { Add } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import CustomSnackbar from '../../../../components/CustomSnackbar';
import TwoActionsAlertDialog from '../../../../components/TwoActionsAlertDialog';
import { getGestioneOperativaAttrezzature } from '../../../../utils/api/aggiornamentoGestioneOperativaApi';
import {
  aggiornaAssegnazioneIot,
  creaAssegnazioneIot,
  getAssegnazioniIot,
  getControlliAssegnabili,
  riattivaAssegnazioneIot,
  rimuoviAssegnazioneIot,
  sospendiAssegnazioneIot,
} from '../../../../utils/api/iot_assegnazioni_api';
import {
  archiviaVoceRimappatura,
  getVociRimappaturaAperte,
} from '../../../../utils/api/iot_rimappature_api';
import {
  CODICE_ATTREZZATURA_GIA_LEGATA,
  CODICE_SONDA_GIA_LEGATA,
  codiceConflittoRegistro,
  eliminaSondaDalRegistro,
  leggiSondaDelRegistro,
  registraSondaSuAttrezzatura,
} from '../../../../utils/api/iot_devices_api';
import AssegnazioneIotModal from './AssegnazioneIotModal';
import AssegnazioniIotTable from './AssegnazioniIotTable';
import ArchiviaVoceModal from './ArchiviaVoceModal';
import AttrezzatureSenzaSondaTable from './AttrezzatureSenzaSondaTable';
import CodaRevisioneTable from './CodaRevisioneTable';
import VociArchiviateTable from './VociArchiviateTable';
import {
  ETICHETTA_DISASSOCIA,
  ETICHETTA_RIATTIVA,
  ETICHETTA_SOSPENDI,
  TITOLO_RIATTIVA,
  TITOLO_SOSPENDI,
  attrezzatureSenzaSonda,
  esitoConfrontoAssegnazione,
  normalizzaAssegnazioni,
  normalizzaControlliAssegnabili,
} from './assegnazioniIotUtils';
import {
  ESITO_VOCE_NON_VERIFICABILE,
  esitoConfrontoVoce,
  normalizzaRevisione,
  normalizzaVociRimappatura,
  vociConProvenienza,
} from './rimappatureUtils';
import { reportApiError } from '../../../../utils/reportApiError';

const useStyles = makeStyles((theme) => ({
  sezione: {
    width: '90%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
  },
  blocco: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(3),
  },
  // Titolo e descrizione stanno insieme, il bottone no: con la descrizione dentro
  // il flex item, `alignItems: center` centra il bottone su un blocco alto due
  // righe — finisce sotto il titolo e si sposta al variare di quante righe la
  // descrizione occupa.
  titolo: {
    display: 'flex',
    flexDirection: 'column',
    // `blocco` separa i suoi figli di 16px: senza un contenitore proprio la
    // descrizione si stacca dal titolo e si legge come se fosse della tabella.
    gap: theme.spacing(0.5),
    // Lo schermo che ospita la sezione centra tutto (`DettaglioEsercizioCommerciale`,
    // regola `mainContainer`) e il tab IoT e' l'unico montato in un `Grid` nudo,
    // senza il `sectionContainer` con cui gli altri tab se ne difendono. Il titolo
    // non se ne accorge — e' un flex item — la descrizione si', ed e' un blocco a
    // tutta larghezza: senza questo esce centrata sotto un titolo a sinistra.
    textAlign: 'left',
  },
  intestazione: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  descrizione: {
    color: theme.palette.text.secondary,
  },
  // The count of open voci, next to the block title: readable without opening the
  // table and without counting rows by hand. A chip and not yellow text, for the
  // usual reason - `warning.main` is a surface, not a text colour (1.1:1 on white
  // against the 4.5:1 of WCAG AA).
  conteggio: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    fontWeight: 600,
    padding: theme.spacing(0.25, 1),
    borderRadius: theme.shape.borderRadius,
    whiteSpace: 'nowrap',
  },
  titoloConConteggio: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
}));

/**
 * The queue is not empty, it is unreadable: two different states, and they have
 * to be told apart. One sentence, one reader (`caricaVoci`), so the mount read
 * and every later re-read cannot drift.
 */
const codaIlleggibile = (error) =>
  (
    'La coda di revisione non è leggibile: quante sonde siano rimaste senza' +
    ` controllo non si sa. ${error?.message ?? ''}`
  ).trim();

const ESITO_OK = 'ok';
const ESITO_OBSOLETA = 'obsoleta';
const ESITO_ERRORE = 'errore';

// Il titolo nomina l'azione per intero, il bottone porta l'etichetta corta che
// sta nella colonna: due registri per lo stesso gesto, e nessuna stringa scritta
// a mano qui — cosi' il bottone della conferma e quello della riga non possono
// divergere.
const CONFERME = {
  sospendi: {
    title: TITOLO_SOSPENDI,
    okLabel: ETICHETTA_SOSPENDI,
    messaggio: (assegnazione) =>
      `L'associazione fra la sonda ${assegnazione.sn} e ${assegnazione.attrezzaturaNome} resta, e potrai riattivarla da qui. Fino ad allora il controllo «${assegnazione.controlloNome}» torna manuale e va compilato a mano.`,
  },
  riattiva: {
    title: TITOLO_RIATTIVA,
    okLabel: ETICHETTA_RIATTIVA,
    messaggio: (assegnazione) =>
      `Il monitoraggio del controllo «${assegnazione.controlloNome}» riprende alla prima temperatura rilevata dalla sonda ${assegnazione.sn} entro i limiti: se l’attrezzatura è già in temperatura, subito. Fino ad allora non vengono inviati avvisi.`,
  },
  disassocia: {
    title: ETICHETTA_DISASSOCIA,
    okLabel: ETICHETTA_DISASSOCIA,
    messaggio: (assegnazione) =>
      `L'associazione fra la sonda ${assegnazione.sn} e ${assegnazione.attrezzaturaNome} viene rimossa e ne resta traccia. Il controllo «${assegnazione.controlloNome}» torna manuale e per tornare indietro dovrai creare una nuova assegnazione. La sonda resta libera: potrai registrarla su un'altra attrezzatura senza sciogliere niente. Se invece vuoi solo fermare il monitoraggio per un po', usa l'icona «${TITOLO_SOSPENDI}» nella colonna Monitoraggio: l'associazione resta e la riattivi da lì.`,
  },
};

// Le due conferme distruttive del registro. Sono due situazioni diverse — la sonda
// e' altrove, oppure l'attrezzatura ha gia' un'altra sonda — e quello che si scioglie
// sta ai due capi opposti del legame, quindi due conferme e non una: ognuna nomina il
// legame che sparisce, che e' l'unica cosa che il tecnico deve poter valutare.
const CONFERMA_REGISTRO = {
  [CODICE_SONDA_GIA_LEGATA]: {
    title: 'La sonda è registrata su un’altra attrezzatura',
    messaggio: ({ macAddress, attrezzaturaScelta, attrezzaturaAttuale }) =>
      attrezzaturaAttuale
        ? `La sonda ${macAddress} è registrata sull’attrezzatura «${attrezzaturaAttuale}». Continuando, quel legame viene sciolto e la sonda passa a «${attrezzaturaScelta}».`
        : `La sonda ${macAddress} è registrata su un’altra attrezzatura. Continuando, quel legame viene sciolto e la sonda passa a «${attrezzaturaScelta}».`,
  },
  // The second sentence is the consequence, and the panel knows it by the time
  // this confirmation is offered: `assegnazioneCheBlocca` has just answered `null`
  // on the chosen attrezzatura, or the branch would not reach here. Unsaid, the
  // confirmation asks to break a link that appears in no table - «Attrezzature
  // senza monitoraggio attivo» is computed from the Assegnazioni, the conflict
  // comes from
  // the registry (`iot_device.equipment_id`), and only the first of the two
  // archives is on screen at all.
  //
  // «di questo esercizio» and not «nessun monitoraggio» outright: the list loaded
  // belongs to one esercizio, so where the two archives have diverged that sonda
  // can hold an Assegnazione elsewhere, invisible from here.
  [CODICE_ATTREZZATURA_GIA_LEGATA]: {
    title: 'L’attrezzatura ha già una sonda registrata',
    messaggio: ({ macAddress, attrezzaturaScelta }) =>
      `L’attrezzatura «${attrezzaturaScelta}» ha già un’altra sonda registrata. Continuando, quel legame viene sciolto e al suo posto va la sonda ${macAddress}. Quella sonda non alimenta nessun controllo di questo esercizio: sciogliendo il legame non si spegne nessun monitoraggio.`,
  },
};

/**
 * Backoffice half of US 7, 8 and 27: the technician associates, reassigns,
 * suspends, reactivates and disassociates from the panel, not only from the app.
 *
 * There is no role check here on purpose: the protection is the tenant, as in
 * the rest of the panel (no ruoli-permesso exist in the product — CONTEXT.md,
 * voce Codice operatore).
 */
const AssegnazioniIotSection = ({
  esercizioId,
  gestioneOperativaId,
  onCodaRevisionata,
}) => {
  const classes = useStyles();

  const [assegnazioni, setAssegnazioni] = useState([]);
  const [attrezzature, setAttrezzature] = useState([]);
  // Il negativo non puo' essere calcolato prima che l'elenco delle attrezzature
  // sia arrivato: finche' e' 'caricamento' la tabella resta in caricamento, e su
  // 'errore' dice che non lo sa invece di dichiarare tutto sorvegliato.
  const [statoAttrezzature, setStatoAttrezzature] = useState('caricamento');
  const [scoperte, setScoperte] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [caricamentoScoperte, setCaricamentoScoperte] = useState(true);
  const [modale, setModale] = useState(null);
  const [erroreForm, setErroreForm] = useState(null);
  const [salvataggioInCorso, setSalvataggioInCorso] = useState(false);
  const [conferma, setConferma] = useState(null);
  const [confermaRegistro, setConfermaRegistro] = useState(null);
  // The review queue: the CHIUSA voci nobody has reviewed yet.
  const [voci, setVoci] = useState([]);
  const [caricamentoVoci, setCaricamentoVoci] = useState(true);
  const [erroreVoci, setErroreVoci] = useState(null);
  // The voci archived in this session, with the revision that closed them. The
  // read route exposes only the *open* voci (`vociAperte`), so until the service
  // publishes the history too this list restarts empty on every reload — which is
  // why the block below is rendered only when it has rows: an empty one would
  // assert that nothing was ever archived. The trace and its revision stay
  // server-side, append-only.
  const [archiviate, setArchiviate] = useState([]);
  const [voceDaArchiviare, setVoceDaArchiviare] = useState(null);
  const [erroreArchiviazione, setErroreArchiviazione] = useState(null);
  // Guards the queue reader against a response landing after the section is gone.
  const montato = useRef(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    montato.current = true;
    return () => {
      montato.current = false;
    };
  }, []);

  const leggiAssegnazioni = useCallback(async () => {
    const risposta = await getAssegnazioniIot(esercizioId);
    return normalizzaAssegnazioni(risposta);
  }, [esercizioId]);

  const caricaAssegnazioni = useCallback(async () => {
    setCaricamento(true);
    try {
      const elenco = await leggiAssegnazioni();
      setAssegnazioni(elenco);
      return elenco;
    } catch (error) {
      reportApiError(error);
      return null;
    } finally {
      setCaricamento(false);
    }
  }, [leggiAssegnazioni]);

  const leggiVoci = useCallback(
    async () =>
      normalizzaVociRimappatura(await getVociRimappaturaAperte(esercizioId)),
    [esercizioId],
  );

  /**
   * A read of its own, not inside the Assegnazioni `Promise.all`: an unreadable
   * queue must not blank the Assegnazioni list, nor the other way round. It is
   * also why the error lives in a state of its own — the table has to be able to
   * say «I do not know» instead of declaring the queue empty.
   *
   * One reader for the mount read and every re-read. Two copies of «how the queue
   * is read» would let the error text or the ordering drift between them, and this
   * is precisely the path that decides whether an unreadable queue looks empty.
   */
  const caricaVoci = useCallback(async () => {
    setCaricamentoVoci(true);
    setErroreVoci(null);
    try {
      const elenco = await leggiVoci();
      if (montato.current) setVoci(elenco);
    } catch (error) {
      if (montato.current) {
        setVoci([]);
        setErroreVoci(codaIlleggibile(error));
      }
    } finally {
      if (montato.current) setCaricamentoVoci(false);
    }
  }, [leggiVoci]);

  useEffect(() => {
    caricaVoci();
  }, [caricaVoci]);

  useEffect(() => {
    let attivo = true;

    const caricaTutto = async () => {
      setCaricamento(true);
      setStatoAttrezzature('caricamento');
      try {
        // Due letture, non quattro: il reparto viaggia con le righe
        // (`IotAssegnazioneVistaDTO.repartoNome`) e con le attrezzature
        // (`AttrezzaturaGestioneOperativaDTO.nomeReparto`), quindi ricostruirlo
        // interrogando `/reparti` e `/locali` era lavoro per un dato che c'era
        // gia'.
        // soloPubblicate: an attrezzatura created inside the draft belongs to
        // the unpublished revision — its server-assigned UUID id is not the one
        // the published manual carries, so no Controllo of the published manual
        // hangs off it and no sonda can feed it (BSS-1573).
        const [elenco, elencoAttrezzature] = await Promise.all([
          leggiAssegnazioni(),
          getGestioneOperativaAttrezzature(gestioneOperativaId, {
            soloPubblicate: true,
          }),
        ]);
        if (!attivo) return;
        setAssegnazioni(elenco);
        setAttrezzature(elencoAttrezzature ?? []);
        setStatoAttrezzature('pronte');
      } catch (error) {
        if (attivo) {
          setStatoAttrezzature('errore');
          reportApiError(error);
        }
      } finally {
        if (attivo) setCaricamento(false);
      }
    };

    caricaTutto();
    return () => {
      attivo = false;
    };
  }, [gestioneOperativaId, leggiAssegnazioni]);

  // The negative half is asked for one equipment at a time, because
  // controlli-assegnabili answers per equipment (contract of BSDEVPLAT-94).
  // Only the uncovered ones are asked about, so the number of requests is the
  // number of equipments of THIS esercizio that have no live Assegnazione —
  // a few dozen at worst, on a screen a human opens by hand. Nothing polls it.
  useEffect(() => {
    let attivo = true;

    if (statoAttrezzature === 'caricamento') {
      // Niente e' ancora noto: dichiarare adesso "tutto sorvegliato" sarebbe una
      // copertura inventata, su una schermata che esiste per mostrare il buco.
      setCaricamentoScoperte(true);
      return undefined;
    }

    if (statoAttrezzature === 'errore') {
      setScoperte([]);
      setCaricamentoScoperte(false);
      return undefined;
    }

    const candidate = attrezzature.filter(
      (attrezzatura) =>
        !assegnazioni.some(
          (assegnazione) =>
            String(assegnazione.attrezzaturaId) === String(attrezzatura.id) &&
            !assegnazione.sospesoDal,
        ),
    );

    if (candidate.length === 0) {
      setScoperte([]);
      setCaricamentoScoperte(false);
      return undefined;
    }

    setCaricamentoScoperte(true);
    Promise.all(
      candidate.map((attrezzatura) =>
        getControlliAssegnabili(attrezzatura.id)
          .then((risposta) => ({
            id: attrezzatura.id,
            controlli: normalizzaControlliAssegnabili(risposta).controlli,
          }))
          // One equipment that cannot be read must not blank out the whole
          // negative list: it is reported as having no assignable Controllo.
          .catch(() => ({ id: attrezzatura.id, controlli: [] })),
      ),
    ).then((esiti) => {
      if (!attivo) return;
      const controlliPerAttrezzatura = esiti.reduce(
        (mappa, esito) => ({ ...mappa, [esito.id]: esito.controlli }),
        {},
      );
      setScoperte(
        attrezzatureSenzaSonda({
          attrezzature,
          assegnazioni,
          controlliPerAttrezzatura,
        }),
      );
      setCaricamentoScoperte(false);
    });

    return () => {
      attivo = false;
    };
  }, [attrezzature, assegnazioni, statoAttrezzature]);

  const notifica = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  /**
   * Archiving and reassigning are the queue's other two writers, and both live in
   * this app. The banner on the publication screen reads the count from a route
   * of its own, in a tab that stays mounted, so without this it would keep showing
   * the pre-review number until a full page reload — and a count that overstates
   * the queue erodes the signal the same way one that understates it does.
   */
  const segnalaRevisione = () => onCodaRevisionata?.();

  /**
   * The panel is not the only writer: bs-safe-app suspends, reactivates and
   * disassociates the same rows from the shop floor. The routes carry no version,
   * so every write re-reads the list first and is abandoned when the row is no
   * longer the one the technician acted on. Vale per ogni scrittura su una riga
   * esistente, riassegnazione compresa: e' anzi quella dove una riga stantia fa
   * piu' danno (si riparenta una riga il cui stato e' cambiato, o si scrive su
   * un id che non esiste piu').
   *
   * `gestisciErrore` esiste perche' il rifiuto del server va dove il tecnico lo
   * puo' correggere: dialog per le azioni di riga, dentro il form per il salva.
   *
   * `esito` may be a thunk returning `{ message, severity }` for a write whose
   * outcome is not known until it has run. One notification and not two: React is
   * 16.13 in legacy mode, so a second `notifica` after this one is a render of its
   * own - the operator would read the success sentence and then watch it be
   * replaced.
   */
  const eseguiScritturaCondizionata = async (
    assegnazione,
    scrittura,
    esito,
    gestisciErrore = reportApiError,
  ) => {
    setSalvataggioInCorso(true);
    try {
      const elencoAggiornato = await leggiAssegnazioni();
      const confronto = esitoConfrontoAssegnazione(
        assegnazione,
        elencoAggiornato,
      );

      if (!confronto.invariata) {
        setAssegnazioni(elencoAggiornato);
        notifica(confronto.messaggio, 'warning');
        return ESITO_OBSOLETA;
      }

      await scrittura();
      await caricaAssegnazioni();
      const annuncio =
        typeof esito === 'function' ? esito() : { message: esito };
      notifica(annuncio.message, annuncio.severity ?? 'success');
      return ESITO_OK;
    } catch (error) {
      gestisciErrore(error);
      return ESITO_ERRORE;
    } finally {
      setSalvataggioInCorso(false);
    }
  };

  const handleConferma = async () => {
    const { tipo, assegnazione } = conferma;
    setConferma(null);

    if (tipo === 'sospendi') {
      await eseguiScritturaCondizionata(
        assegnazione,
        () => sospendiAssegnazioneIot(assegnazione.id),
        'Monitoraggio sospeso. Il controllo torna manuale.',
      );
      return;
    }

    if (tipo === 'riattiva') {
      await eseguiScritturaCondizionata(
        assegnazione,
        () => riattivaAssegnazioneIot(assegnazione.id),
        'Monitoraggio riattivato.',
      );
      return;
    }

    let avvisoRegistro = null;
    await eseguiScritturaCondizionata(
      assegnazione,
      async () => {
        await rimuoviAssegnazioneIot(assegnazione.id);
        avvisoRegistro = await sciogliLegameDiRegistro(assegnazione);
      },
      () =>
        avvisoRegistro
          ? { message: avvisoRegistro, severity: 'warning' }
          : { message: 'Sonda disassociata. Il controllo torna manuale.' },
    );
  };

  /**
   * The second half of a disassociation: the Sonda-Attrezzatura link of the
   * registry, which is what the glossary names after the gesture. `RIMOSSA` of
   * ADR-0012 travels on the first write, and `eliminaRegistro` refuses while a live
   * Assegnazione stands on the sonda (BSDEVPLAT-124), so the order is fixed - this
   * runs after `rimuoviAssegnazioneIot`, and only then can succeed. ADR-0017 keeps
   * one live Assegnazione per sonda, so removing that one leaves nothing behind to
   * hold the row down.
   *
   * Without it the registry row outlives the Assegnazione on every disassociation:
   * the attrezzatura goes back into «Attrezzature senza monitoraggio attivo» and
   * the next sonda associated there comes back as ERR-004, over a link no surface
   * of this
   * panel names - `iot_device` is never read by attrezzatura, only by MAC inside
   * the ERR-003 branch, which runs after the write.
   *
   * Returns the sentence to show instead of the success one, or `null`. The two
   * writes are not a transaction and the first cannot be undone, so a refusal here
   * is not a failed disassociation: the Controllo is manual, the row is gone from
   * the table, and what is left is a registry link the operator now knows about.
   */
  const sciogliLegameDiRegistro = async (assegnazione) => {
    try {
      await eliminaSondaDalRegistro(assegnazione.sn);
      return null;
    } catch (error) {
      return (
        `Il controllo «${assegnazione.controlloNome}» torna manuale, ma la sonda` +
        ` ${assegnazione.sn} resta registrata su «${assegnazione.attrezzaturaNome}»:` +
        ` associando un'altra sonda a «${assegnazione.attrezzaturaNome}» ti verrà` +
        ` chiesto di sciogliere quel legame. ${error?.message ?? ''}`
      ).trim();
    }
  };

  /**
   * Archives a voce: the probe is not reassigned, and the written reason stays on
   * record next to the trace.
   *
   * Conditional write as for the Assegnazioni, and for the same reason: the queue
   * has a second writer in production — another workstation archiving the same
   * voce, and bs-safe-app reassigning the probe from the shop floor, because
   * creating an Assegnazione closes the voci of that `sn` in the same transaction
   * (`IotAssegnazioneCrudService.crea`). The uniqueness constraint on
   * `rimappatura_id` holds the race server-side anyway; this re-read is here so the
   * wrong message cannot win — «archived» on a voce somebody else closed by
   * reassigning the probe is the opposite of what happened.
   *
   * The re-read is caught apart from the write on purpose. Wrapped together, a
   * failed *read* put its message in the archive form's error slot, and the
   * operator read it as the archive being refused — while it is the guard, not the
   * server, that stopped it. Three outcomes, not two: unchanged, already reviewed,
   * and state-not-verifiable.
   */
  const handleArchivia = async (ragione) => {
    const voce = voceDaArchiviare;
    setErroreArchiviazione(null);
    setSalvataggioInCorso(true);

    let elencoAggiornato = null;
    try {
      elencoAggiornato = await leggiVoci();
    } catch (error) {
      // The archive stays available on retry: nothing was written, and the reason
      // the operator typed is still in the field.
      setErroreArchiviazione(
        'Non è stato possibile verificare lo stato di questa voce prima di' +
          ` archiviarla: riprova. ${error?.message ?? ''}`.trimEnd(),
      );
      setSalvataggioInCorso(false);
      return;
    }

    const confronto = esitoConfrontoVoce(voce, elencoAggiornato);
    if (confronto.esito === ESITO_VOCE_NON_VERIFICABILE) {
      setErroreArchiviazione(confronto.messaggio);
      setSalvataggioInCorso(false);
      return;
    }

    if (!confronto.invariata) {
      setVoci(elencoAggiornato);
      setVoceDaArchiviare(null);
      notifica(confronto.messaggio, 'warning');
      setSalvataggioInCorso(false);
      return;
    }

    try {
      const revisione = normalizzaRevisione(
        await archiviaVoceRimappatura(voce.id, ragione),
      );
      // On top: the last archived one is the one being reasoned about.
      setArchiviate((precedenti) => [{ ...voce, revisione }, ...precedenti]);
      setVoceDaArchiviare(null);
      await caricaVoci();
      segnalaRevisione();
      notifica('Voce archiviata. La sonda resta senza controllo.');
    } catch (error) {
      // The refusal stays in the form, where the modal stays open and the written
      // reason is not lost.
      setErroreArchiviazione(error?.message);
    } finally {
      setSalvataggioInCorso(false);
    }
  };

  // The server refusals (deleted Controllo, Controllo the manual does not link,
  // missing quantity or Limiti, second Assegnazione on the same probe) belong in
  // the form, next to the field the technician can change. Anche il 403: e' un
  // rifiuto di autorizzazione con un suo messaggio, non una sessione scaduta.
  const mostraErroreNelForm = (error) => setErroreForm(error?.message);

  const handleSalvaAssegnazione = async (dati) => {
    setErroreForm(null);

    if (modale?.assegnazione) {
      const esito = await eseguiScritturaCondizionata(
        modale.assegnazione,
        () =>
          aggiornaAssegnazioneIot(modale.assegnazione.id, {
            controlloId: dati.controlloId,
            modalita: dati.modalita,
          }),
        'Assegnazione aggiornata.',
        mostraErroreNelForm,
      );
      // Solo il rifiuto del server tiene aperta la modale: se la riga non e' piu'
      // quella, il form mostrerebbe dati che non esistono piu'.
      if (esito !== ESITO_ERRORE) setModale(null);
      return;
    }

    await associa(dati);
  };

  /**
   * Un gesto per il tecnico, due scritture per il server, in quest'ordine.
   * `POST /api/iot/assegnazioni` non registra la sonda: la risolve a partire
   * dall'attrezzatura e rifiuta se non la trova
   * (`IotAssegnazioneCrudService.sondaRegistrata`). Il legame sonda-attrezzatura lo
   * scrive il registro, quindi va scritto prima — e non e' una transazione: se la
   * registrazione passa e l'Assegnazione viene rifiutata, la sonda resta registrata
   * e non assegnata, ed e' quello che il messaggio deve dire.
   */
  const associa = async (dati, forceDetach = false) => {
    // One `setSalvataggioInCorso(false)` for both writes, in the outer `finally`.
    // React is 16.13 in legacy mode (`ReactDOM.render`), so updates after an
    // `await` are not batched: a flag turned off between the first write and the
    // second is a render of its own, with the spinner gone and `Salva` clickable
    // again in the middle of two writes that are not a transaction. The early
    // `return`s of the registration `catch` pass through here too.
    setSalvataggioInCorso(true);
    try {
      try {
        await registraSondaSuAttrezzatura({
          macAddress: dati.macAddress,
          attrezzaturaId: dati.attrezzaturaId,
          forceDetach,
        });
      } catch (error) {
        const codice = codiceConflittoRegistro(error);
        // With `forceDetach` already on, a conflict is no longer an offer: the
        // technician has accepted, so the refusal is a refusal.
        if (codice && !forceDetach) {
          const registro = await attrezzaturaDelRegistro(
            codice,
            dati.macAddress,
          );
          // The two conflicts are opposite, so the live Assegnazione that blocks
          // them stands at opposite ends of the link: on ERR-003 on the
          // attrezzatura the sonda is registered on now, on ERR-004 on the one
          // just chosen.
          //
          // On ERR-004 the sonda the server refuses for is the one of the registry
          // row, while what is read here is the Assegnazione of the *attrezzatura*:
          // the two archives can diverge, which is the premise of this whole
          // screen. Where they have, a forcing the server would have accepted is
          // refused here - the benign side of the same divergence, and the reason
          // the message below names only what `bloccante` proves.
          const bloccante = assegnazioneCheBlocca(
            codice === CODICE_SONDA_GIA_LEGATA
              ? (registro?.id ?? null)
              : dati.attrezzaturaId,
          );
          // Offering `Sciogli e associa` here would promise a move the server
          // refuses with 400: the technician accepts and nothing happens. The panel
          // holds what it takes to know that before the write.
          //
          // The message goes in the form and not into a third confirmation: there
          // is nothing to confirm, and this way the modal stays open and filled in
          // - the technician leaves, disassociates, and comes back to a full form
          // instead of an empty one.
          if (bloccante) {
            mostraErroreNelForm({
              message:
                codice === CODICE_SONDA_GIA_LEGATA
                  ? `La sonda ${dati.macAddress} alimenta «${bloccante.controlloNome}» su ` +
                    `«${bloccante.attrezzaturaNome}». Disassociala da lì, poi torna qui.`
                  : `«${bloccante.attrezzaturaNome}» ha già la sonda ${bloccante.sn} assegnata a ` +
                    `«${bloccante.controlloNome}». Disassocia quella, poi torna qui.`,
            });
            return;
          }
          setConfermaRegistro({
            codice,
            dati,
            macAddress: dati.macAddress,
            attrezzaturaScelta: nomeAttrezzatura(dati.attrezzaturaId),
            attrezzaturaAttuale: registro?.nome ?? null,
          });
          return;
        }
        mostraErroreNelForm(error);
        return;
      }

      try {
        await creaAssegnazioneIot(dati);
        notifica('Sonda associata.');
        setModale(null);
        // The queue is re-read after *every* creation, not only after those started
        // from one of its rows: the server closes the voci by `sn`
        // (`revisionaPerRiassegnazione`) inside the new row's transaction, and there
        // is no close route to call from here.
        await Promise.all([caricaAssegnazioni(), caricaVoci()]);
        segnalaRevisione();
      } catch (error) {
        // The two writes are not a transaction: here the first went through and
        // the second did not, so the state is not the one the form started from
        // and the message says so - otherwise the technician cannot tell where to
        // resume.
        setErroreForm(
          `${error?.message} La sonda resta registrata sull’attrezzatura ma non assegnata: correggi e riprova da qui.`,
        );
      }
    } finally {
      setSalvataggioInCorso(false);
    }
  };

  const nomeAttrezzatura = (attrezzaturaId) =>
    attrezzature.find(
      (attrezzatura) => String(attrezzatura.id) === String(attrezzaturaId),
    )?.nome ?? '';

  /**
   * Su quale attrezzatura la sonda e' registrata adesso, per la conferma di
   * `ERR-003`. Se la lettura non riesce la conferma resta, senza il nome: dire «su
   * un'altra attrezzatura» e' meno di quanto si vorrebbe, e sempre meglio che
   * chiedere di sciogliere un legame senza dire che ne esiste uno.
   */
  const attrezzaturaDelRegistro = async (codice, macAddress) => {
    if (codice !== CODICE_SONDA_GIA_LEGATA) return null;
    try {
      const device = await leggiSondaDelRegistro(macAddress);
      return device?.attrezzatura ?? null;
    } catch {
      return null;
    }
  };

  /**
   * L'Assegnazione viva che rende impossibile sciogliere il legame di registro, o
   * `null` se non ce n'e' e la forzatura puo' riuscire.
   *
   * `forceDetach` salta il conflitto e chiama `eliminaRegistro`, che si rifiuta di
   * cancellare la riga finche' sulla sonda vive un'Assegnazione (BSDEVPLAT-124): e'
   * `IotAssegnazioneCrudService` il solo scrittore che emette il trigger `RIMOSSA`
   * di ADR-0012, quindi una cancellazione a cascata dal registro lascerebbe
   * iot-compliance ad allarmare su una sonda che nessuno ha assegnato. Il rifiuto e'
   * voluto: la forzatura copre il conflitto di *solo registro*.
   *
   * Il confronto e' sull'attrezzatura e non sul `sn` apposta: l'accoppiamento
   * riga-attrezzatura e' unico, e confrontare qui il MAC digitato con il `sn`
   * canonico dell'elenco vorrebbe dire normalizzare lato client, che e' cio' che
   * ADR-0013 assegna al server.
   */
  const assegnazioneCheBlocca = (attrezzaturaId) =>
    attrezzaturaId == null
      ? null
      : (assegnazioni.find(
          // `String()` on both sides as everywhere else on this screen: the two
          // sources disagree on the type of the id (`getAttrezzature` answers
          // strings, `getAssegnazioniIot` numbers), and a `===` between them never
          // matches — so the pre-emptive ERR-004 branch would never fire and the
          // technician would be offered «Sciogli e associa» for a detach the
          // server rejects with 400.
          (assegnazione) =>
            String(assegnazione.attrezzaturaId) === String(attrezzaturaId),
        ) ?? null);

  // The queue labels are computed against the published manual's equipment. A
  // miss does not prove the attrezzatura is gone — `ResetIdsStep` renumbers on
  // cascade (ADR-0008) — which is why `rimappatureUtils` says «id not resolvable»
  // and not «no longer in the manual».
  const righeCoda = vociConProvenienza({ voci, attrezzature });
  // `vociConProvenienza` spreads the voce, so `revisione` survives the call: no
  // index-zip back onto `archiviate` is needed, and none should be there — it
  // would break the day that helper filters or sorts.
  const righeArchiviate = vociConProvenienza({
    voci: archiviate,
    attrezzature,
  });

  return (
    <div className={classes.sezione}>
      <Paper className={classes.blocco}>
        <div className={classes.titolo}>
          <div className={classes.intestazione}>
            <Typography variant="h5">Sonde</Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              disabled={caricamento}
              onClick={() => {
                setErroreForm(null);
                setModale({
                  assegnazione: null,
                  attrezzaturaPreselezionataId: null,
                });
              }}
            >
              Nuova assegnazione
            </Button>
          </div>
          <Typography variant="body2" className={classes.descrizione}>
            Chi sorveglia cosa in questo esercizio: sonda, attrezzatura, reparto
            e controllo alimentato.
          </Typography>
        </div>
        <AssegnazioniIotTable
          assegnazioni={assegnazioni}
          loading={caricamento}
          onModifica={(assegnazione) => {
            setErroreForm(null);
            setModale({ assegnazione, attrezzaturaPreselezionataId: null });
          }}
          onSospendi={(assegnazione) =>
            setConferma({ tipo: 'sospendi', assegnazione })
          }
          onRiattiva={(assegnazione) =>
            setConferma({ tipo: 'riattiva', assegnazione })
          }
        />
      </Paper>

      {/* The review queue of the orphaned Assegnazioni (US 33). It lives here, in
          the esercizio detail, because answering «riassegna» IS creating an
          Assegnazione: the same two writes, and the same registry conflicts, as
          the block above. */}
      <Paper className={classes.blocco} data-testid="blocco-coda-revisione">
        <div className={classes.titolo}>
          <div className={classes.titoloConConteggio}>
            <Typography variant="h5">Assegnazioni da rivedere</Typography>
            {/* The count reads without opening the table. Only when there is
                something to review: a «0 da rivedere» would be a yellow alarm
                over an empty queue. */}
            {!caricamentoVoci && !erroreVoci && righeCoda.length > 0 && (
              <Typography
                variant="body2"
                component="span"
                className={classes.conteggio}
              >
                {`${righeCoda.length} da rivedere`}
              </Typography>
            )}
          </div>
          <Typography variant="body2" className={classes.descrizione}>
            Sonde che la pubblicazione del manuale ha lasciato senza controllo:
            quelle attrezzature non sono più sorvegliate e nessun allarme lo
            dirà. Riassegna la sonda a un controllo del manuale nuovo, oppure
            archivia la voce con una ragione.
          </Typography>
        </div>
        <CodaRevisioneTable
          voci={righeCoda}
          loading={caricamentoVoci}
          errore={erroreVoci}
          onRiassegna={(riga) => {
            setErroreForm(null);
            setModale({
              assegnazione: null,
              attrezzaturaPreselezionataId: null,
              // The trace's canonical `sn` is the only form of the key the queue
              // carries, and canonicalising an already canonical one is idempotent
              // server-side (ADR-0013): the client normalises nothing.
              snPreselezionato: riga.sn,
            });
          }}
          onArchivia={(riga) => {
            setErroreArchiviazione(null);
            setVoceDaArchiviare(riga);
          }}
        />
      </Paper>

      {/* Only when it has rows. The service exposes no read route for reviewed
          voci, so this list restarts empty on every reload: an empty table under
          a «Voci archiviate» heading would look like history and state that
          nothing was ever archived, which is worse than no table. */}
      {righeArchiviate.length > 0 && (
        <Paper className={classes.blocco} data-testid="blocco-voci-archiviate">
          <div className={classes.titolo}>
            <Typography variant="h5">Archiviate in questa sessione</Typography>
            <Typography variant="body2" className={classes.descrizione}>
              Archiviate da qui in questa sessione, con la ragione scritta, chi
              l’ha scritta e quando. La traccia e la sua revisione restano a
              storico lato server e non si cancellano: ricaricando la pagina
              questo elenco riparte vuoto, la voce archiviata no.
            </Typography>
          </div>
          <VociArchiviateTable voci={righeArchiviate} />
        </Paper>
      )}

      <Paper className={classes.blocco} data-testid="blocco-senza-sonda">
        <div className={classes.titolo}>
          {/* Two situations land in this table, and what they share is neither a
              missing Assegnazione nor a missing sonda: a suspended Assegnazione
              exists, with its identity intact, and that is what ADR-0010
              guarantees. What is true of both is that no sonda is feeding those
              Controlli *now* - so the title says that, and nothing about what
              exists. Neither «senza sonda», which would assert something about
              `iot_device` that the panel never reads, nor «senza Assegnazione»,
              which the suspended row reading one table above contradicts. */}
          <Typography variant="h5">
            Attrezzature senza monitoraggio attivo
          </Typography>
          <Typography variant="body2" className={classes.descrizione}>
            Hanno controlli assegnabili che nessuna sonda sta alimentando: o non
            hanno un’assegnazione, o ne hanno una sospesa. In entrambi i casi
            restano manuali. Compaiono solo le attrezzature dell’ultima
            pubblicazione della Gestione Operativa: quelle aggiunte in bozza si
            potranno assegnare dopo la pubblicazione.
          </Typography>
        </div>
        <AttrezzatureSenzaSondaTable
          attrezzature={scoperte}
          loading={caricamentoScoperte}
          errore={
            statoAttrezzature === 'errore'
              ? "Le attrezzature dell'esercizio non sono leggibili: quali restano senza monitoraggio non si sa."
              : null
          }
          onAssocia={(riga) => {
            setErroreForm(null);
            setModale({
              assegnazione: null,
              attrezzaturaPreselezionataId: riga.attrezzaturaId,
            });
          }}
        />
      </Paper>

      {modale && (
        <AssegnazioneIotModal
          open
          assegnazione={modale.assegnazione}
          attrezzature={attrezzature}
          attrezzaturaPreselezionataId={modale.attrezzaturaPreselezionataId}
          snPreselezionato={modale.snPreselezionato}
          erroreServer={erroreForm}
          salvataggioInCorso={salvataggioInCorso}
          onClose={() => {
            setModale(null);
            setErroreForm(null);
          }}
          onSalva={handleSalvaAssegnazione}
          onDisassocia={(assegnazione) => {
            setModale(null);
            setErroreForm(null);
            setConferma({ tipo: 'disassocia', assegnazione });
          }}
        />
      )}

      {voceDaArchiviare && (
        <ArchiviaVoceModal
          open
          voce={voceDaArchiviare}
          erroreServer={erroreArchiviazione}
          salvataggioInCorso={salvataggioInCorso}
          onClose={() => {
            setVoceDaArchiviare(null);
            setErroreArchiviazione(null);
          }}
          onArchivia={handleArchivia}
        />
      )}

      {confermaRegistro && (
        <TwoActionsAlertDialog
          visible
          title={CONFERMA_REGISTRO[confermaRegistro.codice].title}
          message={CONFERMA_REGISTRO[confermaRegistro.codice].messaggio(
            confermaRegistro,
          )}
          okLabel="Sciogli e associa"
          indietroLabel="Annulla"
          onIndietroClicked={() => setConfermaRegistro(null)}
          onOkClicked={() => {
            const { dati } = confermaRegistro;
            setConfermaRegistro(null);
            associa(dati, true);
          }}
        />
      )}

      {conferma && (
        <TwoActionsAlertDialog
          visible
          title={CONFERME[conferma.tipo].title}
          message={CONFERME[conferma.tipo].messaggio(conferma.assegnazione)}
          okLabel={CONFERME[conferma.tipo].okLabel}
          indietroLabel="Annulla"
          onIndietroClicked={() => setConferma(null)}
          onOkClicked={handleConferma}
        />
      )}

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
};

AssegnazioniIotSection.propTypes = {
  esercizioId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  gestioneOperativaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  // Called after every successful review write, so the count shown on the
  // publication screen does not go stale while the operator works the queue.
  onCodaRevisionata: PropTypes.func,
};

export default AssegnazioniIotSection;
